import { api } from './api'
import type { LoginCredentials, AuthResponse, Usuario, Rol } from '../types'

/**
 * El backend devuelve el tipo en mayúsculas (CONFORMITY | CLIENTE).
 * El frontend trabaja en minúsculas para mantener compatibilidad con los guards
 * y el diseño existente (CSS capitalize, comparaciones === 'conformity', etc.).
 */
function normalizeRol(rol: string): Rol {
  return rol.toLowerCase() as Rol
}

export const authService = {
  /**
   * POST /auth/login
   * Body: { identifier, password, rememberMe? }
   * Respuesta backend: { success: true, data: { accessToken, expiresIn, tokenType, usuario } }.
   * El refresh token viaja exclusivamente en una cookie HttpOnly.
   */
  login: (credentials: LoginCredentials): Promise<AuthResponse> =>
    api.post('/auth/login', credentials).then((r) => {
      const { accessToken, usuario } = r.data.data as {
        accessToken:  string
        usuario: {
          id:        string
          nombre:    string
          apellido?: string | null
          username?: string | null
          telefono?: string | null
          email:     string
          rol:       string
          tenant_id: string | null
          nivel_acceso: 'LECTURA' | 'ESCRITURA'
        }
      }
      return {
        accessToken,
        user: {
          id:        usuario.id,
          nombre:    usuario.nombre,
          apellido:  usuario.apellido ?? null,
          username:  usuario.username ?? null,
          telefono:  usuario.telefono ?? null,
          email:     usuario.email,
          rol:       normalizeRol(usuario.rol),
          tenant_id: usuario.tenant_id ?? null,
          nivel_acceso: usuario.nivel_acceso,
        },
      }
    }),

  /**
   * POST /auth/refresh
   * La cookie HttpOnly se envía automáticamente; el body queda vacío.
   * Respuesta backend: { success: true, data: { accessToken, expiresIn, tokenType } }
   */
  refresh: (): Promise<{ accessToken: string }> =>
    api.post('/auth/refresh', {}).then((r) => ({
      accessToken: r.data.data.accessToken as string,
    })),

  /**
   * GET /auth/me — requiere Bearer JWT
   * Respuesta backend: { success: true, data: { id, nombre, email, rol, tenant_id } }
   */
  me: (): Promise<Usuario> =>
    api.get('/auth/me').then((r) => {
      const u = r.data.data as Usuario & { rol: string }
      return {
        ...u,
        rol: normalizeRol(u.rol),
        tenant_id: u.tenant_id ?? null,
        nivel_acceso: u.nivel_acceso,
      }
    }),

  /**
   * POST /auth/logout — requiere Bearer JWT y usa la cookie HttpOnly.
   * El refresh token se revoca en el servidor; el Bearer garantiza que es el usuario legítimo.
   */
  logout: (): Promise<void> =>
    api.post('/auth/logout', {}).then(() => undefined),

  /**
   * POST /auth/forgot-password
   * Body: { email }
   * El backend es anti-enumeración: siempre devuelve 200 aunque el email no exista.
   */
  forgotPassword: (email: string): Promise<void> =>
    api.post('/auth/forgot-password', { email }).then(() => undefined),

  /**
   * POST /auth/reset-password
   * Body: { token, newPassword }
   * El campo se llama `newPassword` en el backend (no `password`).
   */
  resetPassword: (token: string, newPassword: string): Promise<void> =>
    api.post('/auth/reset-password', { token, newPassword }).then(() => undefined),
}
